import type { TFunction } from 'i18next';
import type { DailyQuestWithProgress } from '../../models/apiTypes';
import { isAdQuest } from '../../utils/quest/questHelpers';

export function normalizeQuestObjectiveType(type: string | undefined): string {
  return (type ?? "").trim().toLowerCase();
}

export interface GetProgressTextParams {
  quest: DailyQuestWithProgress;
  watchedAdQuestIds: Set<number>;
  t: TFunction;
}

// Функція для отримання тексту прогресу квесту
export const getProgressText = ({ quest, watchedAdQuestIds, t }: GetProgressTextParams): { text: string; taskDescription: string; taskImages?: string[]; isCompleted: boolean; progressLabel: string } => {
  if (!quest.objectives || quest.objectives.length === 0) return { text: "", taskDescription: "", isCompleted: false, progressLabel: "" };

  const objective = quest.objectives[0];
  const ot = normalizeQuestObjectiveType(objective.type);
  // Для квестів з рекламою: якщо реклама переглянута локально, вважаємо завершеним одразу
  const questIsAdQuest = isAdQuest(quest.quest_id, objective.type);
  const wasAdWatched = questIsAdQuest && quest.user_quest_id && watchedAdQuestIds.has(quest.user_quest_id); // quest тут з API, до маппінгу
  const isCompleted = wasAdWatched || objective.is_completed || objective.current_count >= objective.target_count;
  
  // Обмежуємо current_count, щоб не показувати більше за target_count (наприклад, 3/1)
  // Для квестів з рекламою: якщо реклама переглянута локально, показуємо target_count
  const displayCurrent = wasAdWatched ? objective.target_count : Math.min(objective.current_count, objective.target_count);
  
  let text = "";
  let taskDescription = "";
  let taskImages: string[] | undefined = undefined;
  let progressLabel = "";
  
  // Визначаємо опис завдання та прогрес для всіх типів квестів
  if (ot === "visit_player_house") {
    taskDescription = t("View other players' buildings");
    // Показуємо різні будинки (1, 2, 3, 4)
    taskImages = [
      "/game-assets/map-objects/real-estate/1.webp",
      "/game-assets/map-objects/real-estate/2.webp",
      "/game-assets/map-objects/real-estate/3.webp",
      "/game-assets/map-objects/real-estate/4.webp",
    ];
    progressLabel = t("Buildings visited:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "tapping") {
    // Три окремі фрази з захардкореними числами для квестів 5, 106, 107
    if (quest.quest_id === 5) {
      taskDescription = t("You need to tap the pearl shell 100 times. This quest may be credited with a delay, just wait");
    } else if (quest.quest_id === 106) {
      taskDescription = t("You need to tap the pearl shell 1000 times. This quest may be credited with a delay, just wait");
    } else if (quest.quest_id === 107) {
      taskDescription = t("You need to tap the pearl shell 10000 times. This quest may be credited with a delay, just wait");
    }
    taskImages = ["/ui/navigation-icons/shell.png"];
    progressLabel = t("Tap the pearl shell:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "visit_static_map_object") {
    // Для квестів з відвідування локацій (City Hall, Shop, Fire Hell)
    if (objective.target_action === "visit_city_hall") {
      taskDescription = t("Visit the Town Hall");
      taskImages = ["/game-assets/map-objects/cityhall.webp"];
      progressLabel = t("Visit Town Hall:");
    } else if (objective.target_action === "visit_shop") {
      taskDescription = t("Visit the Shop");
      taskImages = ["/game-assets/map-objects/shop.webp"];
      progressLabel = t("Visit the Shop:");
    } else if (objective.target_action === "visit_hell_of_flame") {
      taskDescription = t("Visit the Hall of Fame");
      taskImages = ["/game-assets/map-objects/hall-of-fame.webp"];
      progressLabel = t("Visit the Hall of Fame:");
    } else {
      taskDescription = t("Visit the location");
      progressLabel = t("Locations visited:");
    }
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "be_subscribed_to_group") {
    taskDescription = t("Subscribe to the Telegram group");
    progressLabel = t("Subscribed to group:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "be_in_chat") {
    taskDescription = t("Join the Telegram chat");
    progressLabel = t("Joined the chat:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "played_partner_miniapp") {
    taskDescription = "";
    progressLabel = t("Visited partner app:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "watch_ad" || ot === "watch_ads" || ot.startsWith("watch_ad_")) {
    taskDescription = t("Please watch the ad until the end");
    progressLabel = t("Ads watched:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "follow_x" || ot.startsWith("follow_x_")) {
    taskDescription = ""; // Не показуємо окремий опис для цього квесту (опис вже є в description)
    progressLabel = t("Subscribe to X:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "register_for_election") {
    taskDescription = t("Register for mayor elections");
    progressLabel = t("Election registrations:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "vote_for_candidate") {
    taskDescription = t("Vote for friends in mayor elections");
    progressLabel = t("Votes cast:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "win_mayor_election") {
    taskDescription = t("Win mayor elections");
    progressLabel = t("Mayor elections won:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "invite_friends") {
    taskDescription = t("Invite friends to the game");
    progressLabel = t("Friends invited:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "buy_furniture") {
    taskDescription = t("Buy furniture from the shop");
    progressLabel = t("Buy furniture:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "remove_cobweb") {
    taskDescription = t("Remove cobwebs in house interiors");
    progressLabel = t("Remove cobweb:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "win_duel") {
    taskDescription = t("Win duels");
    progressLabel = t("Duels won:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "write_to_bot") {
    progressLabel = t("Sent message to bot:");
    text = `${displayCurrent}/${objective.target_count}`;
  } else if (ot === "talk_to_mayor") {
    taskDescription = t("Talk to the mayor");
    progressLabel = t("Talk to the mayor:");
    text = `${displayCurrent}/${objective.target_count}`;
  }
  
  // Спеціальна обробка для квесту написання боту (quest_id 172) - показуємо прогрес
  if (quest.quest_id === 172 && !text) {
    progressLabel = t("Sent message to bot:");
    text = `${displayCurrent}/${objective.target_count}`;
  }

  if (!text && objective.target_count > 0) {
    text = `${displayCurrent}/${objective.target_count}`;
  }
  if (text && !progressLabel) {
    progressLabel = t("Steps completed:");
  }
  
  return { text, taskDescription, taskImages, isCompleted, progressLabel };
};
